import '@/Monitoring';
import 'simplebar-react/dist/simplebar.min.css';
import '@/stores/init';

import { handleRequestError, IgnoreError } from '@/error-handler';
import * as route from '@/constants/route';
import useUserStore from '@/stores/useUserStore';
import anewSignIn from '@/utils/anewSignIn';
import Axios from '@/utils/Axios';
import { useNotice } from '@/utils/notice';
import { css, injectGlobal } from '@emotion/css';
import { defineApp } from '@umijs/max';
import { App, ConfigProvider } from 'antd';
import zhCN from 'antd/locale/zh_CN';
import { PropsWithChildren } from 'react';
import { SWRConfig } from 'swr';
import { hasSubject } from '@/utils/subject';
import { useShallow } from 'zustand/shallow';
import useThemeStore from './stores/useThemeStore';
import { getProfile } from './requests/useProfile';
import Logger from './utils/Logger';
import isIpePlatform from './utils/isIpePlatform';
import downSvg from './images/down.svg';

// eslint-disable-next-line @typescript-eslint/no-unused-expressions
injectGlobal`
  #root {
    height: 100%;
  }
  
  input:-webkit-autofill,
  input:-webkit-autofill:focus {
    transition: background-color 0s 600000s, color 0s 600000s !important;
  }
  
  .rc-virtual-list-scrollbar.rc-virtual-list-scrollbar-vertical {
    width: 6px !important;
  }
  .rc-virtual-list-scrollbar-thumb {
    background-color: rgba(0, 0, 0, 0.1)!important;
    border-radius: 4px !important;
  }

  .ant-cascader-menu {
    ::-webkit-scrollbar {
      width: 6px;
    }
    ::-webkit-scrollbar-thumb {
      background-color: rgba(0, 0, 0, 0.1);
      border-radius: 4px;
    }
  }

  .ant-select .ant-select-arrow {
    width: 12px;
    height: 12px;
    background: url(${downSvg}) no-repeat center / contain;
  }

  .ant-select .ant-select-arrow .anticon,
  .ant-select .ant-select-arrow svg {
    display: none;
  }

 .ant-modal-content {
    margin-bottom: 80px;
  }
`;

function Providers(props: PropsWithChildren) {
  const { children } = props;

  const [
    antdThemeColor,
    antdPrimaryColor,
    antdPrimaryHoverColor,
    filterBorderColor,
    filterHoverBodrderColor,
    filterActiveBorderColor,
    antdDefaultBoderColor,
    antdDefaultHoverBoderColor,
    antdDefaultHoverTextColor,
    antdDefaultHoverBgColor,
  ] = useThemeStore(
    useShallow((s) => [
      s.antdThemeColor,
      s.antdPrimaryColor,
      s.antdPrimaryHoverColor,
      s.filterBorderColor,
      s.filterHoverBodrderColor,
      s.filterActiveBorderColor,
      s.antdDefaultBoderColor,
      s.antdDefaultHoverBoderColor,
      s.antdDefaultHoverTextColor,
      s.antdDefaultHoverBgColor,
    ]),
  );

  return (
    <SWRConfig
      value={{
        shouldRetryOnError: false,
        revalidateOnFocus:
          process.env.UMI_APP_SWR_REVALIDATE_ON_FOCUS !== 'false',
        onError: (error) => {
          if (error.isAxiosError) {
            handleRequestError(error);
            throw new IgnoreError();
          }

          throw error;
        },
      }}
    >
      <ConfigProvider
        locale={zhCN}
        theme={{
          token: {
            colorPrimary: antdThemeColor || '#1677FF',
            colorBgSpotlight: 'rgba(47, 50, 53, 1)',
          },
          components: {
            Table: {
              headerSplitColor: 'transparent',
            },
            Modal: {
              borderRadiusLG: 12,
              titleFontSize: 16,
              titleLineHeight: '22px',
              titleColor: 'rgba(37, 40, 43, 1)',
            },
            Input: {
              fontSize: 13,
              colorText: 'rgba(37, 40, 43, 0.7)',
              colorTextPlaceholder: 'rgba(37, 40, 43, 0.4)',
              colorBorder: filterBorderColor,
              hoverBorderColor: filterHoverBodrderColor,
              activeBorderColor: filterActiveBorderColor,
            },
            Select: {
              fontSize: 13,
              optionFontSize: 13,
              colorText: 'rgba(37, 40, 43, 0.7)',
              colorTextPlaceholder: 'rgba(37, 40, 43, 0.4)',
              optionSelectedFontWeight: 400,
              colorBorder: filterBorderColor,
              hoverBorderColor: filterHoverBodrderColor,
              activeBorderColor: filterActiveBorderColor,
            },
            Button: {
              fontSize: 13,
              colorText: 'rgba(37, 40, 43, 0.7)',
              colorTextPlaceholder: 'rgba(37, 40, 43, 0.4)',
              colorBorder: filterBorderColor,
              colorPrimaryHover: filterHoverBodrderColor,
              colorPrimaryActive: filterActiveBorderColor,
            },
            Cascader: {
              fontSize: 13,
              optionSelectedFontWeight: 400,
            },
          },
        }}
        modal={{
          className: css`
            .ant-modal-footer,
            .ant-modal-confirm-btns {
              .ant-btn {
                background: ${antdPrimaryColor} !important;
                color: rgba(255, 255, 255, 1);
                &:hover {
                  background: ${antdPrimaryHoverColor}!important;
                }
              }
              .ant-btn-primary {
                background: ${antdPrimaryColor} !important;
                background: ${antdPrimaryColor} !important;
                color: rgba(255, 255, 255, 1);
                &:disabled {
                  background: ${antdPrimaryColor} !important;
                  opacity: 0.1;
                }
                &:hover {
                  background: ${antdPrimaryHoverColor} !important;
                }
              }
              .ant-btn-default {
                background: #ffffff !important;
                color: rgba(37, 40, 43, 1);
                border: 1px solid ${antdDefaultBoderColor};
                &:hover {
                  border: 1px solid ${antdDefaultHoverBoderColor} !important;
                  background: ${antdDefaultHoverBgColor} !important;
                  color: ${antdDefaultHoverTextColor} !important;
                }
              }
            }
          `,
        }}
      >
        {children}
      </ConfigProvider>
    </SWRConfig>
  );
}

async function loadSchool() {
  const { data } = await Axios.get<
    {
      current: boolean;
      id: number;
      name: string;
      role: string;
    }[]
  >('/teachers/schools');

  useUserStore.setState({
    schools: data.map((v) => ({
      id: String(v.id),
      name: v.name,
    })),
    currentSchoolId: String(data.find((v) => v.current)?.id || ''),
  });
}

let alreadyLoadedUserProfile = false;

async function loadUserProfile() {
  if (alreadyLoadedUserProfile && useUserStore.getState().isFilled()) return;

  try {
    const profile = await getProfile();
    useUserStore.setState({
      id: String(profile.id),
      name: profile.name,
      prefer: profile.prefer || false,
      unit: profile.unit,
      section: profile.section,
    });

    await loadSchool();

    alreadyLoadedUserProfile = true;
  } catch (e) {
    Logger.debug(e, '加载用户信息失败');

    anewSignIn();
  }
}

function AppExtends() {
  useNotice();
  return null;
}

export default defineApp({
  rootContainer: (container) => (
    <Providers>
      <App>
        <AppExtends />
        {container}
      </App>
    </Providers>
  ),
  onRouteChange: () => {
    if (
      isIpePlatform() &&
      [
        route.classroomCourseware,
        route.intelligenceCourseware,
        route.intelligenceResource,
      ].some((v) => window.location.pathname.includes(v))
    ) {
      window.location.href = '/not-found';
    }

    if (
      ![new RegExp(`/${route.loginPrefix}`)].every((v) =>
        v.test(window.location.pathname),
      )
    ) {
      if (!hasSubject()) {
        anewSignIn('没有选择学科，请重新登录');
        return;
      }
    }

    if (localStorage.getItem('token') && !useUserStore.getState().isFilled()) {
      loadUserProfile();
    }
  },
  render: async (oldRender) => {
    if (localStorage.getItem('token')) {
      await loadUserProfile();
    }

    oldRender();
  },
});
